/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { FilterValue } from './types';

/**
 * HUMAN LABELS FOR REFINEMENT VALUES THE RESPONSE SENDS RAW.
 *
 * `refine-default` renders `value.label || value.value`. That is correct for
 * every group whose values carry a display name — Designer, Color, Size and
 * Price all do — and it falls through to the raw wire value for any group whose
 * refinement definition was imported without one. On 2026-08-23 exactly one
 * group is in that state, and it was shipping the strings `true` and `false`
 * into the rail as user-facing filter labels on all five rails, both brands.
 *
 * Measured on the running app at 1280 (BG handbags, rail container's direct
 * children, checkbox ids read off the rendered `for` attributes):
 *
 *     refine-c_isSale-0   "true(5)"
 *     refine-c_isSale-1   "false(17)"
 *
 * WHAT LIVE CALLS THEM IS MEASURED, NOT CHOSEN — this was briefed to me as
 * unmeasurable because every facet group in the captures is collapsed and
 * renders no children. That is true of the RAIL, and it is not the only place
 * live states the answer. Live NM serialises its whole facet model into the
 * page, expanded or not, and separately ships the strings as bundle constants:
 *
 *   .reference/dumps/nm-plp-d-1280/dt-plp-1280-nm.html
 *     {"filterKey":"Sale","displayText":"Sale",
 *      "facets":{"On Sale":7146,"Regular":16071},
 *      "facetsOrderList":["On Sale","Regular"]}
 *
 *   .reference/dumps/nm-plp-d-1280/.../client.1d13a43c681c2672401e.bundle.js
 *     t.REGULAR_SALE="Regular", t.ONSALE_SALE="On Sale", t.SALE="Sale"
 *
 * Two independent statements in live's own payload, agreeing, and reproduced in
 * the 440 capture (`nm-plp-m-440`) as well as the 1280 one. `facetsOrderList`
 * also fixes the ORDER as On Sale then Regular, which is the order the sandbox
 * already returns, so nothing here reorders anything.
 *
 * BG IS UNMEASURED AND INHERITS NM'S WORDING. BG's bundle carries a
 * `product_sale_type` ANALYTICS tag reading `Clearance` / `Sale` / `Regular`,
 * which is a tracking value on a product and NOT a facet option label — citing
 * it as one would be the same wrong-element error this rail has already paid
 * for. No BG capture on disk expands a Sale group. A BG capture that does would
 * settle it; until then both brands render live NM's strings, which is the only
 * measured wording available and is certainly better than `true`.
 *
 * WHY THE MAP HOLDS THE ENGLISH AND THE LOCALE KEY BOTH. The keys below live in
 * the `categoryRefinements` namespace under `src/locales/**`, which this change
 * does not own, so they do not exist yet. Passing the measured string as
 * i18next's `defaultValue` makes the rail correct on this commit and makes the
 * locale entry an override rather than a prerequisite — the mechanism degrades
 * to the right answer if the key never lands, instead of painting a raw key.
 * Delete the `live` field here once the keys exist.
 *
 * THE DURABLE FIX IS UPSTREAM OF THIS FILE. A refinement value's display name
 * is catalogue data; `c_isSale` has none because the refinement definition was
 * imported without one. Authoring those display names in SFCC would delete this
 * map. It is not done here because it writes to a sandbox three agents are
 * measuring against, which is not a thing to do mid-wave.
 */
const REFINEMENT_VALUE_LABELS: Readonly<
    Record<string, Readonly<Record<string, { readonly key: string; readonly live: string }>>>
> = {
    c_isSale: {
        true: { key: 'categoryRefinements:refinementValues.c_isSale.true', live: 'On Sale' },
        false: { key: 'categoryRefinements:refinementValues.c_isSale.false', live: 'Regular' },
    },
};

/** Minimal shape of i18next's `t`, so this module does not depend on the hook. */
type Translate = (key: string, options: { defaultValue: string }) => string;

/**
 * Apply human labels to a group's values, leaving every value the response
 * already named untouched.
 *
 * THE EMPTINESS TEST IS `label === value`, NOT `!label`, and that distinction
 * is the whole function. The obvious guard — keep any value that has a `label`
 * — reads correctly and is completely inert here, because SCAPI does not omit
 * the field. It echoes the wire value into it. Measured off the running app's
 * own router data, BG handbags at 1280:
 *
 *     { attributeId: 'c_isSale', label: 'Sale',
 *       values: [ { hitCount:  5, label: 'true',  value: 'true'  },
 *                 { hitCount: 17, label: 'false', value: 'false' } ] }
 *
 * So a `!value.label` guard would have shipped a mapping that never once ran,
 * with a green unit test beside it. A label that merely restates the wire value
 * is not a display name, and treating it as one is what put `true` on the rail
 * in the first place.
 *
 * An imported display name still wins the moment it appears — that is the point
 * of comparing rather than overwriting — so this becomes a no-op on its own the
 * day the catalogue carries the names, without anyone editing it.
 */
export function labelRefinementValues(attributeId: string, values: FilterValue[], t: Translate): FilterValue[] {
    const group = REFINEMENT_VALUE_LABELS[attributeId];
    if (!group) return values;

    return values.map((value) => {
        const raw = String(value.value);
        const named = value.label && value.label !== raw;
        if (named) return value;
        const mapped = group[raw];
        return mapped ? { ...value, label: t(mapped.key, { defaultValue: mapped.live }) } : value;
    });
}
